import type { SpecialtyFieldDef, SpecialtyFieldType } from '@/types/api'

/** Parsed defaultValue after Zod wire normalization. */
export type SpecialtyFieldDefaultValue = string | boolean | number | string[]

/**
 * Normalizes catalog `default` / `defaultValue` from agent_specialties.config_schema.
 * Skillforge seeds use `default: []` / `default: {}`; house convention uses `defaultValue`.
 */
export function normalizeSpecialtyDefaultValue(
  fieldType: SpecialtyFieldType | undefined,
  raw: unknown,
): SpecialtyFieldDefaultValue | undefined {
  if (raw === undefined || raw === null) return undefined

  if (fieldType === 'multiselect') {
    if (Array.isArray(raw)) return raw.map((item) => String(item))
    return []
  }

  if (fieldType === 'json') {
    if (typeof raw === 'string') return raw
    if (typeof raw === 'object') return JSON.stringify(raw, null, 2)
    return String(raw)
  }

  if (fieldType === 'boolean') return Boolean(raw)

  if (fieldType === 'number') {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  }

  if (typeof raw === 'object') return JSON.stringify(raw)
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw)
  }

  return undefined
}

/** Preprocesses a raw config_schema field descriptor before Zod object parse. */
export function preprocessSpecialtyFieldDef(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw

  const d = raw as Record<string, unknown>
  const type = d.type as SpecialtyFieldType | undefined
  const rawDefault = d.defaultValue !== undefined ? d.defaultValue : d.default
  const defaultValue = normalizeSpecialtyDefaultValue(type, rawDefault)

  const { default: _legacy, ...rest } = d
  if (defaultValue === undefined) return rest
  return { ...rest, defaultValue }
}

/** Value for form state / textarea display when user has not set the field yet. */
export function specialtyValueForUi(
  field: SpecialtyFieldDef,
  value: unknown,
): unknown {
  if (value !== undefined && value !== null && value !== '') return value
  if (field.defaultValue === undefined) return value

  if (field.type === 'json') {
    if (typeof field.defaultValue === 'string') return field.defaultValue
    if (typeof field.defaultValue === 'object') {
      return JSON.stringify(field.defaultValue, null, 2)
    }
    return String(field.defaultValue)
  }

  if (field.type === 'multiselect' && Array.isArray(field.defaultValue)) {
    return field.defaultValue
  }

  return field.defaultValue
}

export function buildSpecialtyConfigDefaults(
  schema: SpecialtyFieldDef[] | null | undefined,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  if (!schema?.length) return defaults

  for (const field of schema) {
    if (field.defaultValue === undefined) continue
    if (field.type === 'json' && typeof field.defaultValue === 'string') {
      defaults[field.key] = field.defaultValue
    } else if (field.type === 'multiselect' && Array.isArray(field.defaultValue)) {
      defaults[field.key] = [...field.defaultValue]
    } else {
      defaults[field.key] = field.defaultValue
    }
  }

  return defaults
}
